export const BLOG_TOPICS = [
  "All",
  "Agencies",
  "Employers",
  "Temps",
  "Compliance",
  "Shift management",
  "Timesheets",
  "Worker availability",
] as const;

export type BlogTopic = (typeof BLOG_TOPICS)[number];

const TOPIC_KEYWORDS: Record<Exclude<BlogTopic, "All">, RegExp> = {
  Agencies: /\bagenc/i,
  Employers: /\bemployer|\bclient|\bbooking/i,
  Temps: /\btemp\b|\btemps\b|\bworker\b|\bcandidate/i,
  Compliance: /complian|right\s+to\s+work|gdpr|wtd|nmw|minimum\s+wage/i,
  "Shift management": /shift|roster|schedul/i,
  Timesheets: /timesheet|payroll|approval/i,
  "Worker availability": /availab/i,
};

export function inferTopic(post: {
  title?: string | null;
  excerpt?: string | null;
}): Exclude<BlogTopic, "All"> | null {
  const hay = `${post.title ?? ""} ${post.excerpt ?? ""}`;
  for (const topic of Object.keys(TOPIC_KEYWORDS) as Array<Exclude<BlogTopic, "All">>) {
    if (TOPIC_KEYWORDS[topic].test(hay)) return topic;
  }
  return null;
}

export function postMatchesTopic(
  post: { title?: string | null; excerpt?: string | null },
  topic: BlogTopic,
): boolean {
  if (topic === "All") return true;
  const re = TOPIC_KEYWORDS[topic];
  return re ? re.test(`${post.title ?? ""} ${post.excerpt ?? ""}`) : true;
}

/**
 * URL-slug mapping for the `?topic=` query parameter.
 *
 * The public "Popular topics" card uses the human-friendly labels on the
 * left; URLs use the slugs on the right. Multiple slugs can map to a single
 * internal taxonomy entry — that's intentional so the public URLs stay
 * stable even if the inferTopic vocabulary changes.
 */
const SLUG_TO_TOPIC: Record<string, Exclude<BlogTopic, "All">> = {
  "worker-availability": "Worker availability",
  "timesheet-approvals": "Timesheets",
  "agency-compliance": "Compliance",
  "shift-fulfilment": "Shift management",
  "employer-bookings": "Employers",
  // Pill-driven slugs (kebab-case of the topic label) for the pill rail:
  agencies: "Agencies",
  employers: "Employers",
  temps: "Temps",
  compliance: "Compliance",
  "shift-management": "Shift management",
  timesheets: "Timesheets",
};

const TOPIC_TO_SLUG: Record<Exclude<BlogTopic, "All">, string> = {
  Agencies: "agencies",
  Employers: "employer-bookings",
  Temps: "temps",
  Compliance: "agency-compliance",
  "Shift management": "shift-fulfilment",
  Timesheets: "timesheet-approvals",
  "Worker availability": "worker-availability",
};

export function topicFromSlug(slug: string | null | undefined): BlogTopic {
  if (!slug) return "All";
  return SLUG_TO_TOPIC[slug.toLowerCase()] ?? "All";
}

export function slugFromTopic(topic: BlogTopic): string | null {
  if (topic === "All") return null;
  return TOPIC_TO_SLUG[topic] ?? null;
}